import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { getModalSwitch, handleGetAgentTypesData } from '@app/store/actions';
import { ModalActionsComponent, PageLoader, PasslyTabs, PasslyTab, PasslyTabContent } from '@app/components';

import AddEditAgentConfig from '../components/addEditAgentConfig.component';
import Oauth2Config from '../components/oauth2Config.component';
import RadiusConfig from '../components/radiusConfig.component';
import WindowsLogonConfig from '../components/windowsLogonConfig.component';
import OpenIDConnectConfig from '../components/openIDConnectConfig.component';
import { IPostAgentConfig, IAgentDetail, IPolicyAuth, AgentTypeEnum, ClientIdPrefixEnum, AgentConfigTypeEnum, IWindowsLogonConfig, IRadiusClient, IClientRoles, ICustomClaims } from '@app/models';
import { active, onPressEnterModal, genericErrorString } from '@app/helpers';
import SwipeableViews from 'react-swipeable-views';
import { agentsService, policiesAuthService } from '@app/services';

export enum AgentViewType {
    addAgentListView = 0,
    addAgentView = 1,
    editAgentView = 2,
    // second tab
    editAgentExtendedView = 3
}

interface IProps {
    startingView: AgentViewType;
    pageRefresh: Function;
    clientRoles?: IClientRoles[];
    editAgentConfig?: IAgentDetail;
    protectedAgentConfig?: any;
    authPolicies?: IPolicyAuth[];
    clientId?: string;
}

const AddAgentDialog = (props: IProps) => {
    // -------------------------------------------
    // ------------------ DATA -------------------
    // -------------------------------------------
    const isEditView = props.startingView === AgentViewType.editAgentView || props.startingView === AgentViewType.editAgentExtendedView;

    const agentTypes = useSelector((state: any) => state.agents.agentTypes);
    const currentOrgId = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const [initialLoad, setInitialLoad] = useState(!isEditView);
    const [selectedAgentType, setSelectedAgentType] = useState<string>(isEditView ? props.editAgentConfig!.AgentType : '');
    const [viewType, setViewType] = useState(props.startingView);
    const [tabIndex, setTabIndex] = useState(props.startingView === AgentViewType.editAgentExtendedView ? 1 : 0);
    const [formValid, setFormValid] = useState(false);
    const [clientId, setClientId] = useState('');
    const [tempId, setTempId] = useState('');
    const [savingChanges, setSavingChanges] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [allAuthPolicies, setAllAuthPolicies] = useState(props.authPolicies !== undefined ? props.authPolicies : []);
    const [downloadLocation, setDownloadLocation] = useState(props.editAgentConfig?.DownloadLocation || '');
    const [creatingClientInProgress, setCreatingClientInProgress] = useState(false);
    const [customClaimsMapping, setCustomClaimsMapping] = useState<ICustomClaims>(isEditView && selectedAgentType === AgentTypeEnum.openidConnect ?
        getCustomClaimsMappingFromConfig(JSON.parse(props.editAgentConfig!.Configuration))
        :
        { Entries: [] }
    );

    const [protectedConfig, setProtectedConfig] = useState<any>({});

    const [customConfig, setCustomConfig] = useState<any>(isEditView ? JSON.parse(props.editAgentConfig!.Configuration) : {});

    const [tempAgent, setTempAgent] = useState<IPostAgentConfig>(isEditView ?
        convertAgentForEdit(props.editAgentConfig!) : {} as IPostAgentConfig);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useEffect(() => {
        if (viewType === AgentViewType.addAgentListView && agentTypes.length === 0) {
            dispatch(handleGetAgentTypesData());
        }
    }, []);

    useEffect(() => {
        if (allAuthPolicies.length === 0) {
            policiesAuthService.getAuthPolicies(handleError, handleSuccessAuthPolicies);
        }
    }, [allAuthPolicies]);

    useEffect(() => {
        if (agentTypes.length > 0) {
            setInitialLoad(false);
        }
    }, [agentTypes]);

    // -------------------------------------------
    // --------------- FUNCTIONS -----------------
    // -------------------------------------------
    const loadSelectedAgent = (agentType: string, defaultIcon: string, downloadLocation: string) => {
        setDownloadLocation(downloadLocation);

        let prefix = '';
        switch (agentType) {
            case AgentTypeEnum.radiusServerDisplay:
                prefix = ClientIdPrefixEnum.radius;
                break;
            case AgentTypeEnum.windowsLogon:
                prefix = ClientIdPrefixEnum.cp;
                break;
            case AgentTypeEnum.oauth2Client:
                prefix = ClientIdPrefixEnum.oauth2;
                break;
            case AgentTypeEnum.authApiDisplay:
                // Displayed value and backend are different so we have to convert to backend value
                agentType = AgentTypeEnum.apiAuth;
                prefix = ClientIdPrefixEnum.api;
                break;
            case AgentTypeEnum.embeddedWebAuthDisplay:
                // Displayed value and backend are different so we have to convert to backend value
                agentType = AgentTypeEnum.webAuth;
                prefix = ClientIdPrefixEnum.web;
                break;
            case AgentTypeEnum.openidConnect:
                prefix = ClientIdPrefixEnum.oidc;
                break;
        }

        setCreatingClientInProgress(true);
        agentsService.createDefault(prefix, currentOrgId, handleLoadSelectedAgentSuccess.bind(null, defaultIcon, agentType), handleError);
    };

    const handleLoadSelectedAgentSuccess = (defaultIcon: string, agentType: string, response: any) => {
        let agent = tempAgent;

        agent.IconUrl = defaultIcon;
        agent.AgentType = agentType;
        setClientId(response.data.internalIdentifier);

        if (response.data.agentId !== undefined) {
            setTempId(response.data.agentId);
        }
        else {
            setTempId(response.data.id);
        }

        if (agentType === AgentTypeEnum.radiusServerDisplay) {
            agentType = AgentTypeEnum.radiusServer;
        }

        setCreatingClientInProgress(false);
        setSelectedAgentType(agentType);
        setViewType(AgentViewType.addAgentView);
    };

    function convertAgentForEdit(agent: IAgentDetail): IPostAgentConfig {
        // We have to do some conversion on the data because the Configuration
        // field is a JSON object that can be multiple different types.
        // Within this configuration is data shown on the generic config page as
        // well as data shown on the unique config tab (radius and windows logon)
        const deserializedConfig = JSON.parse(agent.Configuration);

        const agentForEdit: IPostAgentConfig = {
            AgentName: agent.AgentName,
            AgentType: agent.AgentType,
            ProvisioningState: agent.ProvisioningState,
            SyncIntervalMinutes: agent.SyncIntervalMinutes.toString(),
            WLA2FATimeoutSeconds: agent.WLA2FATimeoutSeconds === null ? 180 : agent.WLA2FATimeoutSeconds.toString(),
            IconUrl: agent.IconUrl,
            RedirectUri: (deserializedConfig === null || deserializedConfig.RedirectUri === null) ? '' : deserializedConfig.RedirectUri as string,
            PolicyId: deserializedConfig === null ? '' : deserializedConfig.PolicyId as string,
            RequiresConsent: deserializedConfig === null ? false : deserializedConfig.RequiresConsent as boolean
        } as IPostAgentConfig;

        return agentForEdit;
    }

    function getCustomClaimsMappingFromConfig(config: any): ICustomClaims {
        if (config.CustomClaimsMapping === null || config.CustomClaimsMapping === undefined) {
            return { Entries: [] };
        }
        else {
            return JSON.parse(config.CustomClaimsMapping) as ICustomClaims;
        }
    }

    const handleSuccessAuthPolicies = (response: any) => {
        setAllAuthPolicies(response.data.value);
        setTempAgent({
            AgentName: '',
            ProvisioningState: active,
            SyncIntervalMinutes: '60',
            RedirectUri: 'https://example.com',
            PolicyId: '',
            RequiresConsent: false,
            WLA2FATimeoutSeconds: '180'
        } as IPostAgentConfig);
    };

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTabIndex(newValue);
    };

    const handleChangeIndex = (index: number) => {
        setTabIndex(index);
    };

    const handleError = (error: any) => {
        setSavingChanges(false);
        setCreatingClientInProgress(false);

        if (error.response.status === 409) {
            setErrorMessage(translation('AGENT_PROFILE_NAME_ALREADY_EXISTS'));
        }
        else {
            setErrorMessage(genericErrorString(error));
        }
    };

    const handleSuccess = (response: any) => {
        setSavingChanges(false);
        props.pageRefresh(true);
        dispatch(getModalSwitch(false));
    };

    const closeConfirmModal = () => {
        if (tempId !== '' && !isEditView) {
            // we precreated the default config, remove it since we're cancelling
            agentsService.deleteUnused(tempId);
        }
        dispatch(getModalSwitch(false));
    };

    const customConfigValid = (valid: boolean) => {
        if (valid && checkGenericConfigValid(tempAgent)) {
            setFormValid(true);
        }
        else {
            setFormValid(false);
        }
    };

    const checkGenericConfigValid = (agentConfig: IPostAgentConfig): boolean => {
        if (agentConfig.AgentName === '' ||
            agentConfig.PolicyId === '' ||
            ((selectedAgentType === AgentTypeEnum.apiAuth ||
                selectedAgentType === AgentTypeEnum.webAuth ||
                selectedAgentType === AgentTypeEnum.openidConnect) &&
                agentConfig.RedirectUri === '')
        ) {
            return false;
        }
        else {
            return true;
        }
    };

    // Changes to the shared agent config page
    const onConfigAgentChange = (agentConfig: IPostAgentConfig) => {
        setFormValid(checkGenericConfigValid(agentConfig));
        setTempAgent(agentConfig);
    };

    // changes to the custom agent config page
    const onConfigurationChange = (configuration: any) => {
        setCustomConfig(configuration);
    };

    // changes to protected config items
    const onProtectedConfigChange = (protectedConfig: any, type: string) => {
        if (type === AgentConfigTypeEnum.radiusConfig) {
            let newProtectedConfig: any = {
                Type: type,
                Clients: protectedConfig
            };

            setProtectedConfig(newProtectedConfig);
        }
        if (type === AgentConfigTypeEnum.windowsLogonConfig) {
            if (protectedConfig === '') {
                setProtectedConfig({});
            }
            else {
                let newProtectedConfig: any = {
                    OverridePassword: protectedConfig
                };

                setProtectedConfig(newProtectedConfig);
            }
        }
    };

    const getAgentTabLabel = (): string => {
        switch (selectedAgentType) {
            case AgentTypeEnum.radiusServer:
                return translation('RADIUS_CONFIGURATION_TAB');
            case AgentTypeEnum.windowsLogon:
                return translation('WINDOWS_LOGON_CONFIGURATION_TAB');
            case AgentTypeEnum.oauth2Client:
                return translation('OAUTH2_CLIENT_CONFIGURATION_TAB');
            case AgentTypeEnum.openidConnect:
                return translation('OIDC_CONFIGURATION_TAB');
            default:
                return '';
        }
    };

    const handleSaveBtn = () => {
        let createAgent: any = {
            SyncIntervalMinutes: tempAgent.SyncIntervalMinutes,
            WLA2FATimeoutSeconds: tempAgent.WLA2FATimeoutSeconds,
            IconUrl: tempAgent.IconUrl,
            AgentName: tempAgent.AgentName,
            ProvisioningState: tempAgent.ProvisioningState,
            DownloadLocation: downloadLocation
        };

        if (selectedAgentType === AgentTypeEnum.radiusServer) {
            createAgent.Configuration = JSON.stringify({
                Type: AgentConfigTypeEnum.radiusConfig,
                RadiusPort: customConfig,
                PolicyId: tempAgent.PolicyId
            });
        }
        else if (selectedAgentType === AgentTypeEnum.windowsLogon) {
            if (!customConfig.HasOverrideGroup) {
                delete customConfig.OverrideGroup;
            }

            createAgent.Configuration = JSON.stringify({
                Type: AgentConfigTypeEnum.windowsLogonConfig,
                ...customConfig,
                PolicyId: tempAgent.PolicyId
            });
        }
        else if (selectedAgentType === AgentTypeEnum.openidConnect) {
            createAgent.Configuration = JSON.stringify({
                Type: AgentConfigTypeEnum.webConfig,
                PolicyId: tempAgent.PolicyId,
                RedirectUri: tempAgent.RedirectUri,
                RequiresConsent: tempAgent.RequiresConsent,
                CustomClaimsMapping: customClaimsMapping.Entries.length > 0 ? JSON.stringify(customClaimsMapping) : null
            });
        }
        else {
            createAgent.Configuration = JSON.stringify({
                Type: AgentConfigTypeEnum.webConfig,
                PolicyId: tempAgent.PolicyId,
                RedirectUri: tempAgent.RedirectUri,
                RequiresConsent: tempAgent.RequiresConsent
            });
        }

        setSavingChanges(true);
        if (viewType === AgentViewType.addAgentView) {
            createAgent.ClientId = clientId;
            createAgent.Version = '1.0';
            createAgent.AgentType = selectedAgentType;
            createAgent['@odata.type'] = 'Kaseya.AuthAnvil.Models.AgentProfile';
            createAgent.OrganizationId = currentOrgId;
            createAgent.RawProtectedConfiguration = JSON.stringify(protectedConfig);
            agentsService.postAgent(createAgent, handleError, handleSuccess);
        }
        else {
            if (selectedAgentType === AgentTypeEnum.radiusServer || selectedAgentType === AgentTypeEnum.windowsLogon) {
                const updatedConfig = { configData: JSON.stringify(protectedConfig) };
                agentsService.updateAgentProtectedConfig(updatedConfig, props.editAgentConfig!.Id)
                    .then(() => {
                        agentsService.updateAgent(createAgent, props.editAgentConfig!.Id, handleError, handleSuccess);
                    });
            }
            else {
                agentsService.updateAgent(createAgent, props.editAgentConfig!.Id, handleError, handleSuccess);
            }
        }
    };

    return (
        <>
            {!initialLoad ?
                <div className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleSaveBtn, !formValid || savingChanges)}>
                    {viewType === AgentViewType.addAgentListView && agentTypes.length > 0 &&
                        <div className='modal__identities-list'>
                            <div className='modal__identities-list--data'>
                                {creatingClientInProgress ?
                                    <PageLoader />
                                    :
                                    <>
                                        {agentTypes.map((agentType: any, index: number) => {
                                            return (
                                                <div key={index}
                                                    className='modal__identities-list--agent'
                                                    id={`authAgent${index.toString()}`}
                                                    onClick={() => loadSelectedAgent(agentType.DisplayName, agentType.IconUrl, agentType.DownloadLocation)}
                                                >
                                                    <div className='modal__identities-list--icon'>
                                                        <img src={agentType.IconUrl} />
                                                    </div>
                                                    <div className='modal__identities-list--app__col'>
                                                        <div className='modal__identities-list--agentname'>{translation(agentType.DisplayName)}</div>
                                                        <div>{translation(agentType.Description)}</div>
                                                    </div>
                                                    <div className='modal__identities-list--app__col' />
                                                    <div className='modal__identities-list--app__col' />
                                                </div>
                                            );
                                        })}
                                    </>
                                }
                            </div>
                        </div>
                    }

                    {(selectedAgentType === AgentTypeEnum.radiusServer || selectedAgentType === AgentTypeEnum.windowsLogon || selectedAgentType === AgentTypeEnum.oauth2Client || selectedAgentType === AgentTypeEnum.openidConnect)
                        ?
                        <>
                            <div className='modal__identities-list-tabs'>
                            <PasslyTabs
                                value={tabIndex}
                                onChange={handleTabChange}
                            >
                                <PasslyTab label={translation('AGENT_CONFIGURATION_TAB')} />
                                <PasslyTab label={getAgentTabLabel()} />
                            </PasslyTabs>
                            </div>
                            <SwipeableViews
                                index={tabIndex}
                                onChangeIndex={handleChangeIndex}
                            >
                                <PasslyTabContent value={tabIndex} index={0}>
                                    <AddEditAgentConfig
                                        agentTypeArg={selectedAgentType}
                                        configDataChanged={onConfigAgentChange}
                                        agentConfigArg={tempAgent}
                                        authPolicies={allAuthPolicies}
                                    />
                                </PasslyTabContent>
                                <PasslyTabContent value={tabIndex} index={1}>
                                    {selectedAgentType === AgentTypeEnum.oauth2Client &&
                                        <Oauth2Config
                                            clientId={isEditView ? props.clientId : clientId}
                                            clientRoles={props.clientRoles ? props.clientRoles : []}
                                            formValidCallback={customConfigValid} />
                                    }
                                    {selectedAgentType === AgentTypeEnum.radiusServer &&
                                        <RadiusConfig
                                            updateConfigCallback={onConfigurationChange}
                                            updateProtectedConfigCallback={onProtectedConfigChange}
                                            authPolicies={allAuthPolicies}
                                            isEditView={isEditView}
                                            editRadiusPort={isEditView ? JSON.parse(props.editAgentConfig!.Configuration)?.RadiusPort as number : 1812}
                                            editClients={props.protectedAgentConfig === undefined ? [] as IRadiusClient[] : props.protectedAgentConfig as IRadiusClient[]}
                                            clientId={clientId}
                                            formValidCallback={customConfigValid} />
                                    }
                                    {selectedAgentType === AgentTypeEnum.windowsLogon &&
                                        <WindowsLogonConfig
                                            editAgent={isEditView ? JSON.parse(props.editAgentConfig!.Configuration) as IWindowsLogonConfig : undefined}
                                            overridePass={props.protectedAgentConfig === undefined ? '' : props.protectedAgentConfig as string}
                                            isEditView={isEditView}
                                            updateConfigCallback={onConfigurationChange}
                                            updateProtectedConfigCallback={onProtectedConfigChange}
                                            formValidCallback={customConfigValid} />
                                    }
                                    {selectedAgentType === AgentTypeEnum.openidConnect &&
                                        <OpenIDConnectConfig
                                            setCustomClaimsMapping={setCustomClaimsMapping}
                                            customClaimsMapping={customClaimsMapping}
                                            formValidCallback={customConfigValid}
                                        />
                                    }
                                </PasslyTabContent>
                            </SwipeableViews>
                        </>
                        :
                        <>
                            {(viewType === AgentViewType.addAgentView || viewType === AgentViewType.editAgentView) &&
                                <AddEditAgentConfig
                                    agentTypeArg={selectedAgentType}
                                    configDataChanged={onConfigAgentChange}
                                    agentConfigArg={tempAgent}
                                    authPolicies={allAuthPolicies}
                                />
                            }
                        </>
                    }
                    {errorMessage !== '' &&
                        <span className='modal__error'>{errorMessage}</span>
                    }
                </div>
                :
                <PageLoader />
            }

            <ModalActionsComponent
                primaryBtn={isEditView ? translation('SAVE_CHANGES') : translation('ADD_AGENT')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={closeConfirmModal}
                disabled={!formValid || savingChanges}
                id='addAgent'
            />
        </>
    );
};

export default AddAgentDialog;